import { useEffect, useRef } from 'react';
import { gsap } from '../lib/gsapSetup';
import SectionTag from '../components/SectionTag';
import { CAPABILITIES } from '../content/siteContent';
import { useReducedMotion } from '../hooks/useMotionPrefs';

// Each capability chapter gets a distinct visual transform — everything stays connected.
const REVEALS = [
  { clip: 'inset(0% 100% 0% 0%)', img: { xPercent: -8 } },
  { clip: 'inset(0% 0% 0% 100%)', img: { scale: 1.3 } },
  { clip: 'inset(100% 0% 0% 0%)', img: { rotate: 4, scale: 1.2 } },
];

export default function Capabilities() {
  const ref = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return;
    const ctx = gsap.context(() => {
      CAPABILITIES.forEach((_, i) => {
        const reveal = REVEALS[i % REVEALS.length];
        gsap.fromTo(
          `.cap-img-wrap-${i}`,
          { clipPath: reveal.clip },
          {
            clipPath: 'inset(0% 0% 0% 0%)',
            duration: 1.3,
            ease: 'power2.out',
            scrollTrigger: { trigger: `.cap-row-${i}`, start: 'top 78%', toggleActions: 'play none none reverse' },
          }
        );
        gsap.fromTo(
          `.cap-img-${i}`,
          { scale: 1.18 },
          {
            scale: 1,
            ease: 'none',
            scrollTrigger: { trigger: `.cap-row-${i}`, start: 'top bottom', end: 'bottom top', scrub: 0.5 },
          }
        );
        gsap.fromTo(
          `.cap-text-${i}`,
          { y: 50, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.9,
            ease: 'power3.out',
            scrollTrigger: { trigger: `.cap-row-${i}`, start: 'top 75%', toggleActions: 'play none none reverse' },
          }
        );
      });
    }, ref);
    return () => ctx.revert();
  }, [reduced]);

  return (
    <section
      ref={ref}
      id="capabilities"
      data-testid="capabilities-section"
      className="relative bg-[#000000] px-5 py-28 md:px-12 md:py-40"
    >
      <SectionTag number="07" label="Capabilities" />

      <div className="mx-auto mt-14 max-w-[1400px] md:mt-20">
        {CAPABILITIES.map((cap, i) => (
          <div
            key={cap.num}
            className={`cap-row cap-row-${i} grid items-center gap-8 border-t border-white/10 py-14 md:grid-cols-2 md:gap-16 md:py-20`}
            data-testid={`capability-chapter-${i}`}
          >
            <div className={`cap-text-${i} ${i % 2 === 1 ? 'md:order-2' : ''}`}>
              <span className="font-display text-5xl font-semibold leading-none text-white/10 md:text-7xl">
                {cap.num}
              </span>
              <h3 className="mt-5 font-display text-2xl font-semibold tracking-tight text-white md:text-3xl">
                {cap.title}
              </h3>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-white/65 md:text-base">{cap.body}</p>
            </div>
            <div
              className={`cap-img-wrap-${i} relative h-[42vh] overflow-hidden md:h-[54vh] ${
                i % 2 === 1 ? 'md:order-1' : ''
              }`}
            >
              <img
                src={cap.image}
                alt={cap.title}
                loading="lazy"
                decoding="async"
                className={`cap-img-${i} h-full w-full object-cover will-change-transform`}
              />
              <div className="absolute inset-0 bg-black/25" />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
